import { createAction, createSlice } from '@reduxjs/toolkit';
import { getSettings, loadSearchExperienceConfig } from 'actions';
import { put, select, takeLatest } from 'redux-saga/effects';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { getLocale } from 'sagas/utils/localeSelectors';
import { wsClient } from 'util/appUtils';
import { BridgeClient } from 'app/service/bridge/BridgeClient';
import { BRIDGE_ERROR } from './BridgeSlice';

const sliceName = 'wizard';

const initialState = {
  selectedMode: undefined,
  stepLoading: false,
  scriptConnected: false,
  connectivityStatus: undefined, // 'checking' | 'success' | 'error'
  connectivityError: undefined,
  connectivityChecked: false,
  connectivityResult: undefined, // { accessible, httpStatus, errorCode, responseTime }
  crawlerSettings: {
    baseUrl: '',
    authType: 'none',
    authLogin: '',
    authPassword: '',
  },
};

export const wizardActivateVisualSearch = createAction(`${sliceName}/wizardActivateVisualSearch`);
export const wizardRequestScriptCheck = createAction(`${sliceName}/wizardRequestScriptCheck`);
export const wizardOpenThemeEditor = createAction(`${sliceName}/wizardOpenThemeEditor`);
export const wizardCheckConnectivity = createAction(`${sliceName}/wizardCheckConnectivity`);
export const wizardSaveCrawlerSettings = createAction(`${sliceName}/wizardSaveCrawlerSettings`);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    wizardChange(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    wizardStartLoading(state) {
      return {
        ...state,
        stepLoading: true,
      };
    },
    wizardFinishLoading(state) {
      return {
        ...state,
        stepLoading: false,
      };
    },
  },
});

export const { actions, reducer } = slice;
export const {
  wizardChange,
  wizardStartLoading,
  wizardFinishLoading,
} = actions;
export default reducer;

// LEGACY-ONLY: This saga is used only by old cloud wizard screens.
// The self-hosted production shell registration path is handled by
// /embedded/app browser-owned bootstrap and does NOT call wsClient.registerShop.
export function* doWizardActivateVisualSearch() {
  try {
    const { token } = yield select(({ settings }) => settings);
    yield put(wizardStartLoading());
    yield put(getSettings(token));
    yield put(loadSearchExperienceConfig(token));
    yield put(wizardFinishLoading());
  } catch (err) {
    console.log('Failed in wizardActivateVisualSearch', err);
    yield put(wizardFinishLoading());
  }
}

function* doWizardRequestScriptCheck() {
  try {
    const { token, platform, shopAddress } = yield select(({ settings }) => settings);
    const locale = yield* getLocale();
    yield put(wizardStartLoading());

    const bridge = new BridgeClient();
    const resp = yield bridge.verifyThemeEmbed({ platform, shopAddress });

    if (resp && resp.error) {
      yield put({ type: BRIDGE_ERROR, payload: { message: resp.error } });
      return;
    }

    const payload = resp && resp.payload ? resp.payload : resp;
    const isConnected = !!(payload && (payload.scriptConnected || payload.connected));

    yield put(wizardChange({ scriptConnected: isConnected }));
    if (isConnected) {
      const message = getLocalizedMessage('notifications.success.integrationComplete', {}, locale);
      yield put(enqueueSuccessSnackbar(message, 2000));
    }
    if (token) {
      yield put(getSettings(token));
      yield put(loadSearchExperienceConfig(token));
    }
  } catch (err) {
    yield put({ type: BRIDGE_ERROR, payload: err });
  } finally {
    yield put(wizardFinishLoading());
  }
}

function* doWizardOpenThemeEditor() {
  try {
    const bridge = new BridgeClient();
    const resp = yield bridge.openThemeEditor();
    if (resp && resp.error) {
      yield put({ type: BRIDGE_ERROR, payload: { message: resp.error } });
    }
  } catch (err) {
    yield put({ type: BRIDGE_ERROR, payload: err });
  }
}

function* doWizardCheckConnectivity() {
  try {
    const { shopId } = yield select(({ settings }) => settings);
    const { crawlerSettings } = yield select(({ wizard }) => wizard);

    yield put(wizardChange({
      connectivityStatus: 'checking',
      connectivityError: undefined,
    }));
    yield put(wizardStartLoading());

    const result = yield wsClient.checkConnectivity(
      shopId,
      crawlerSettings.baseUrl,
      crawlerSettings.authType,
      crawlerSettings.authLogin,
      crawlerSettings.authPassword,
    );

    if (result && result.accessible) {
      yield put(wizardChange({
        connectivityStatus: 'success',
        connectivityChecked: true,
        connectivityResult: result,
      }));
    } else {
      yield put(wizardChange({
        connectivityStatus: 'error',
        connectivityChecked: true,
        connectivityResult: result,
        connectivityError: result ? result.errorCode : 'UNKNOWN_ERROR',
      }));
    }
  } catch (err) {
    console.log('Failed in wizardCheckConnectivity', err);
    yield put(wizardChange({
      connectivityStatus: 'error',
      connectivityChecked: true,
      connectivityError: err.message || 'NETWORK_ERROR',
    }));
  } finally {
    yield put(wizardFinishLoading());
  }
}

function* doWizardSaveCrawlerSettings({ payload }) {
  try {
    const { shopId } = yield select(({ settings }) => settings);
    const locale = yield* getLocale();

    yield put(wizardStartLoading());

    const settingsToUpdate = {
      crawler: {
        baseUrl: payload.baseUrl || null,
        authType: payload.authType || 'none',
        authLogin: payload.authLogin || null,
        authPassword: payload.authPassword || null,
      },
    };

    // Use wsClient.updateShopSettings
    const result = yield wsClient.updateShopSettings(shopId, settingsToUpdate);
    if (result?.success === false || (Array.isArray(result?.errors) && result.errors.length > 0)) {
      const first = result.errors?.[0];
      throw new Error(first?.message || first?.error || result?.error
        || getLocalizedMessage('wizard.crawler.saveFailed', {}, locale));
    }

    yield put(wizardChange({
      crawlerSettings: {
        baseUrl: payload.baseUrl || '',
        authType: payload.authType || 'none',
        authLogin: payload.authLogin || '',
        authPassword: payload.authPassword || '',
      },
    }));

    // Reload settings to get updated values
    yield put(getSettings(shopId));
    yield put(loadSearchExperienceConfig(shopId));
    yield put(enqueueSuccessSnackbar(getLocalizedMessage('wizard.crawler.saveSuccess', {}, locale), 2000));
  } catch (err) {
    console.log('Failed in wizardSaveCrawlerSettings', err);
    const locale = yield* getLocale();
    yield put(enqueueErrorSnackbar(
      err.message || getLocalizedMessage('wizard.crawler.saveFailed', {}, locale),
      4000,
    ));
  } finally {
    yield put(wizardFinishLoading());
  }
}

export function* sagas() {
  yield takeLatest(wizardActivateVisualSearch().type, doWizardActivateVisualSearch);
  yield takeLatest(wizardRequestScriptCheck().type, doWizardRequestScriptCheck);
  yield takeLatest(wizardOpenThemeEditor().type, doWizardOpenThemeEditor);
  yield takeLatest(wizardCheckConnectivity().type, doWizardCheckConnectivity);
  yield takeLatest(wizardSaveCrawlerSettings().type, doWizardSaveCrawlerSettings);
}
